import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import useFlashStore from '@shop/store/flash';
import { showApiError, showTranslatedFlashError } from '@shop/utils/ApiError';
import { generateUrl } from '@shop/utils/Routing';

import type {
  CartItem,
  CartResponse,
  CrossSellProduct,
  TotalRow,
  UpdateQtyResponse,
} from './cart.types';

interface CheckoutCartState {
  cart?: {
    items?: CartItem[];
  };
  [key: string]: unknown;
}

interface AppState {
  Cart?: {
    _qty?: number;
  };
  [key: string]: unknown;
}

const useCartStore = defineStore(
  'cart',
  () => {
    // ── Badge state (existing, kept for CartHeader.vue) ──
    const qty = ref<number>(0);
    const isSyncingFromStorage = ref<boolean>(false);

    const cartQty = computed(() => qty.value);

    function setQty(quantity: number): void {
      if (quantity < 0) {
        return;
      }
      qty.value = quantity;
    }

    function calculateQtyFromItems(items: { qty?: number }[]): number {
      if (!Array.isArray(items)) {
        return 0;
      }
      return items.reduce((acc, item) => {
        const itemQty = item.qty ?? 0;
        return acc + itemQty;
      }, 0);
    }

    function syncQtyToStorage(): void {
      if (isSyncingFromStorage.value) {
        return;
      }

      try {
        const localAppState = localStorage.getItem('app');
        if (localAppState) {
          const appState = JSON.parse(localAppState) as AppState;
          appState.Cart ??= {};
          // eslint-disable-next-line no-underscore-dangle
          appState.Cart._qty = qty.value;
          localStorage.setItem('app', JSON.stringify(appState));
        } else {
          const newAppState: AppState = { Cart: { _qty: qty.value } };
          localStorage.setItem('app', JSON.stringify(newAppState));
        }
      } catch {
        // Skip error
      }
    }

    function syncFromCartItems(items: CartItem[]): void {
      const calculatedQty = calculateQtyFromItems(items);
      setQty(calculatedQty);

      try {
        const checkoutState = localStorage.getItem('checkout');
        if (checkoutState) {
          const checkout = JSON.parse(checkoutState) as CheckoutCartState;
          checkout.cart ??= {};
          checkout.cart.items = items;
          localStorage.setItem('checkout', JSON.stringify(checkout));
        }
      } catch {
        // Skip error
      }

      syncQtyToStorage();
    }

    function syncQtyFromStorage(): void {
      isSyncingFromStorage.value = true;
      try {
        const localAppState = localStorage.getItem('app');
        if (localAppState) {
          const appState = JSON.parse(localAppState) as AppState;
          const appCart = appState.Cart;
          if (appCart !== undefined) {
            // eslint-disable-next-line no-underscore-dangle -- persisted Cart._qty in localStorage app
            const appQty = appCart._qty;
            if (typeof appQty === 'number' && appQty >= 0) {
              qty.value = appQty;
              isSyncingFromStorage.value = false;
              return;
            }
          }
        }

        const checkoutState = localStorage.getItem('checkout');
        if (checkoutState) {
          const checkout = JSON.parse(checkoutState) as CheckoutCartState;
          if (checkout.cart?.items && Array.isArray(checkout.cart.items)) {
            const calculatedQty = calculateQtyFromItems(checkout.cart.items);
            if (calculatedQty >= 0) {
              qty.value = calculatedQty;
              isSyncingFromStorage.value = false;
              return;
            }
          }
        }

        const cartBadge = document.querySelector('.cartheader__cart-badge');
        if (cartBadge) {
          const text = cartBadge.textContent?.trim() ?? '0';
          const parsedQty = parseInt(text, 10);
          if (!Number.isNaN(parsedQty) && parsedQty >= 0) {
            qty.value = parsedQty;
          }
        }
      } catch {
        const cartBadge = document.querySelector('.cartheader__cart-badge');
        if (cartBadge) {
          const text = cartBadge.textContent?.trim() ?? '0';
          const parsedQty = parseInt(text, 10);
          if (!Number.isNaN(parsedQty) && parsedQty >= 0) {
            qty.value = parsedQty;
          }
        }
      } finally {
        isSyncingFromStorage.value = false;
      }
    }

    function setupStorageListener() {
      window.addEventListener('storage', event => {
        if (event.key === 'app' || event.key === 'checkout') {
          syncQtyFromStorage();
        }
      });

      let lastLocalAppState: string | null = null;
      let lastCheckoutState: string | null = null;

      const checkAppState = (): void => {
        try {
          const currentCheckoutState = localStorage.getItem('checkout');
          if (currentCheckoutState !== lastCheckoutState && currentCheckoutState) {
            lastCheckoutState = currentCheckoutState;
            const checkout = JSON.parse(currentCheckoutState) as CheckoutCartState;
            if (checkout.cart?.items) {
              syncQtyFromStorage();
            }
          }

          const currentLocalAppState = localStorage.getItem('app');

          if (currentLocalAppState !== lastLocalAppState && currentLocalAppState) {
            lastLocalAppState = currentLocalAppState;
            try {
              const appState = JSON.parse(currentLocalAppState) as AppState;
              const appCart = appState.Cart;
              if (appCart !== undefined) {
                // eslint-disable-next-line no-underscore-dangle -- persisted Cart._qty in localStorage app
                const storedQty = appCart._qty;
                if (typeof storedQty === 'number' && storedQty >= 0) {
                  qty.value = storedQty;
                  return;
                }
              }
            } catch {
              // Skip parse errors
            }
            syncQtyFromStorage();
          }
        } catch {
          // Skip error
        }
      };

      const intervalId = setInterval(checkAppState, 500);

      window.addEventListener('focus', () => {
        syncQtyFromStorage();
      });

      return () => {
        clearInterval(intervalId);
      };
    }

    // ── Full cart page state ──
    const items = ref<CartItem[]>([]);
    const totals = ref<TotalRow[]>([]);
    const crossSells = ref<CrossSellProduct[]>([]);
    const addForFreeShipping = ref<number | null>(null);
    const isLoading = ref(false);
    const updatingItemIds = ref<Set<number>>(new Set());
    const error = ref<string | null>(null);

    const totalQty = computed(() => items.value.reduce((sum, item) => sum + item.qty, 0));

    function applyCartData(data: CartResponse | UpdateQtyResponse) {
      items.value = data.items;
      totals.value = data.totals;
      if ('add_for_free_shipping' in data) {
        addForFreeShipping.value = data.add_for_free_shipping;
      }
      if ('cross_sells' in data) {
        crossSells.value = data.cross_sells;
      }
      setQty(items.value.reduce((sum, item) => sum + item.qty, 0));
    }

    async function fetchCart(): Promise<void> {
      isLoading.value = true;
      error.value = null;
      try {
        const response = await axios.get<CartResponse>(generateUrl('cart'));
        applyCartData(response.data);
        crossSells.value = response.data.cross_sells ?? [];
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load cart';
        // eslint-disable-next-line no-console
        console.error('Failed to fetch cart', err);
      } finally {
        isLoading.value = false;
      }
    }

    async function updateItemQty(quoteItemId: number, newQty: number): Promise<void> {
      error.value = null;
      updatingItemIds.value = new Set([...updatingItemIds.value, quoteItemId]);
      try {
        const response = await axios.post<UpdateQtyResponse>(
          generateUrl('checkout_update_qty', { quoteItem: quoteItemId }),
          { qty: newQty }
        );
        applyCartData(response.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.data) {
          const data = err.response.data as Partial<UpdateQtyResponse>;
          if (data.items) {
            items.value = data.items;
          }
          if (data.totals) {
            totals.value = data.totals;
          }
          if (data.add_for_free_shipping !== undefined) {
            addForFreeShipping.value = data.add_for_free_shipping;
          }
        }
        await showApiError(err, useFlashStore().error);
        // eslint-disable-next-line no-console
        console.error('Failed to update item qty', err);
      } finally {
        const next = new Set(updatingItemIds.value);
        next.delete(quoteItemId);
        updatingItemIds.value = next;
      }
    }

    async function removeItem(quoteItemId: number): Promise<void> {
      await updateItemQty(quoteItemId, 0);
    }

    function getProductQtyInCart(productId: number): number {
      return items.value
        .filter(item => item.product.entity_id === productId)
        .reduce((sum, item) => sum + item.qty, 0);
    }

    async function addCrossSellProduct(productId: number, productQty = 1): Promise<void> {
      error.value = null;
      const qtyBefore = getProductQtyInCart(productId);

      try {
        await axios.post(generateUrl('add_product'), {
          products: [{ product: productId, qty: productQty }],
        });
        await fetchCart();

        if (getProductQtyInCart(productId) < qtyBefore + productQty) {
          await showTranslatedFlashError(
            'exception.not_enough_stock',
            useFlashStore().error,
            'checkout'
          );
        }
      } catch (err) {
        await showApiError(err, useFlashStore().error);
        // eslint-disable-next-line no-console
        console.error('Failed to add cross-sell product', err);
      }
    }

    // ── Lifecycle: storage listeners + badge persistence ──
    if (typeof window !== 'undefined') {
      setupStorageListener();

      watch(
        qty,
        () => {
          syncQtyToStorage();
        },
        { immediate: false }
      );
    }

    syncQtyFromStorage();

    return {
      // Badge (existing API)
      qty,
      cartQty,
      setQty,
      syncFromCartItems,
      syncQtyFromStorage,
      syncQtyToStorage,

      // Full cart state
      items,
      totals,
      crossSells,
      addForFreeShipping,
      isLoading,
      updatingItemIds,
      error,
      totalQty,

      // Actions
      fetchCart,
      updateItemQty,
      removeItem,
      addCrossSellProduct,
    };
  },
  {
    persist: {
      storage: localStorage,
      key: 'cart',
      pick: ['qty'],
    },
  }
);

export default useCartStore;
